import { useEffect, useRef } from 'react';
import { Toaster, toast } from 'sonner';
import { AlertTriangle, CircleAlert, CircleCheck, Info } from 'lucide-react';
import { useEditor } from '../../state/store';

const ICONS = {
  success: <CircleCheck size={15} />,
  warn: <AlertTriangle size={15} />,
  error: <CircleAlert size={15} />,
  info: <Info size={15} />,
} as const;

/**
 * Notifications.
 *
 * The store stays free of any UI library — it records messages and nothing
 * more, which is what keeps it testable under Node. This component subscribes
 * to that list and hands each new message to Sonner, which owns the stacking,
 * the timers, swipe-to-dismiss and the hover-to-hold behaviour.
 */
export function Notifications(): JSX.Element {
  // Messages already shown. Without this, every unrelated store update would
  // re-raise the whole backlog.
  const seen = useRef(new Set<number>());

  useEffect(() => {
    const show = (messages: ReturnType<typeof useEditor.getState>['messages']) => {
      for (const m of messages) {
        if (seen.current.has(m.id)) continue;
        seen.current.add(m.id);
        const fn = m.kind === 'warn' ? toast.warning : m.kind === 'error' ? toast.error : m.kind === 'success' ? toast.success : toast;
        fn(m.text, {
          icon: ICONS[m.kind],
          duration: m.kind === 'error' ? 8000 : 4000,
        });
      }
    };

    show(useEditor.getState().messages);
    return useEditor.subscribe((state, prev) => {
      if (state.messages !== prev.messages) show(state.messages);
    });
  }, []);

  return (
    <Toaster
      theme="dark"
      position="bottom-right"
      // Clears the status bar, which is 26px tall.
      offset={40}
      gap={8}
      visibleToasts={4}
      toastOptions={{ className: 'app-toast' }}
    />
  );
}
